import {
  Component, HostListener, inject, signal, computed, effect, OnInit, OnDestroy,
  ChangeDetectionStrategy, AnimationCallbackEvent,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ConceptService, ConceptResponse, CreateConceptRequest, UpdateConceptRequest } from '../../core/services/concept';
import { ApplicationService, RegisteredAppResponse } from '../../core/services/application';
import { SUPPORTED_CONTENT_LANGUAGES } from '../../core/constants/supported-languages';

@Component({
  selector: 'app-concepts',
  imports: [FormsModule, ReactiveFormsModule, RouterLink],
  templateUrl: './concepts.html',
  styleUrl: './concepts.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConceptsComponent implements OnInit, OnDestroy {
  private conceptService = inject(ConceptService);
  private appService = inject(ApplicationService);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private appsReady = signal(false);
  readonly routeSlug = toSignal(
    this.route.paramMap.pipe(map(params => params.get('appSlug') ?? '')),
    { initialValue: this.route.snapshot.paramMap.get('appSlug') ?? '' },
  );
  readonly routeConceptId = toSignal(
    this.route.paramMap.pipe(map(params => params.get('conceptId') ?? '')),
    { initialValue: this.route.snapshot.paramMap.get('conceptId') ?? '' },
  );

  private syncFromRoute = effect(() => {
    if (!this.appsReady()) return;
    const slug = this.routeSlug();
    const conceptId = this.routeConceptId();
    this.syncSelection(slug, this.apps());
    if (conceptId) this.loadConceptForEdit(conceptId);
  });

  // ── Data ──────────────────────────────────────────────────────────────────
  concepts = signal<ConceptResponse[]>([]);
  apps = signal<RegisteredAppResponse[]>([]);
  isLoading = signal(false);
  appsLoading = signal(false);
  errorMsg = signal<string | null>(null);
  selectedAppId = signal<string>('');
  selectedTag = signal<string>('');

  selectedApp = computed(() => this.apps().find(a => a.app_id === this.selectedAppId()) ?? null);

  appName(appId: string): string {
    return this.apps().find(app => app.app_id === appId)?.app_name ?? '';
  }
  appQuery = signal('');
  filteredApps = computed(() => {
    const query = this.appQuery().trim().toLocaleLowerCase('tr');
    const list = this.apps();
    if (!query) return list;
    return list.filter(app => app.app_name.toLocaleLowerCase('tr').includes(query));
  });

  availableTags = computed(() => {
    const tags = new Set<string>();
    for (const concept of this.concepts()) {
      for (const tag of concept.tags ?? []) {
        const value = tag.trim();
        if (value) tags.add(value);
      }
    }
    return [...tags].sort((a, b) => a.localeCompare(b));
  });

  filteredConcepts = computed(() => {
    const tag = this.selectedTag();
    if (!tag) return this.concepts();
    return this.concepts().filter(concept => (concept.tags ?? []).some(item => item.trim() === tag));
  });

  appInfoLine(app: RegisteredAppResponse): string {
    const parts = [app.platform, app.category].filter(Boolean);
    return parts.join(' · ') || 'App';
  }

  promptMeta(concept: ConceptResponse): string {
    return [...(concept.tags ?? []), ...(concept.languages ?? [])].filter(Boolean).join(' · ');
  }

  // ── Zero-shot inference ───────────────────────────────────────────────────
  inferenceLanguage = signal<string>('');
  inferenceLoading = signal(false);
  inferenceResult = signal<unknown>(null);
  inferenceError = signal<string | null>(null);

  runInference(): void {
    const concept = this.selectedConcept();
    if (!concept) return;
    const lang = this.inferenceLanguage() || undefined;
    this.inferenceLoading.set(true);
    this.inferenceResult.set(null);
    this.inferenceError.set(null);
    this.conceptService.testZeroShot(concept.concept_id, lang).subscribe({
      next: result => { this.inferenceResult.set(result); this.inferenceLoading.set(false); },
      error: err => { this.inferenceError.set(err?.error?.detail ?? 'Inference failed.'); this.inferenceLoading.set(false); },
    });
  }

  inferenceView = computed((): InferenceView | null => {
    const r = this.inferenceResult();
    if (r === null) return null;
    if (typeof r !== 'object') {
      return { kind: 'raw', text: String(r) };
    }
    const data = r as ZeroShotResult;
    const hasArticle = typeof data.generated_title === 'string' || typeof data.generated_content === 'string';
    if (!hasArticle) {
      try { return { kind: 'raw', text: JSON.stringify(r, null, 2) }; } catch { return { kind: 'raw', text: String(r) }; }
    }
    const paragraphs = (data.generated_content ?? '')
      .split(/\n\s*\n/)
      .map(paragraph => paragraph.trim())
      .filter(Boolean);
    return {
      kind: 'article',
      title: (data.generated_title ?? '').trim(),
      paragraphs,
      language: data.language ?? '',
      model: data.model ?? '',
      promptSent: (data.prompt_sent ?? '').trim(),
      json: JSON.stringify(r, null, 2),
    };
  });

  // ── Modal state ───────────────────────────────────────────────────────────
  showCreateModal = signal(false);
  createClosing = signal(false);
  appMenuOpen = signal(false);
  appMenuClosing = signal(false);
  tagMenuOpen = signal(false);
  tagMenuClosing = signal(false);
  private createCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private appMenuTimer: ReturnType<typeof setTimeout> | null = null;
  private tagMenuTimer: ReturnType<typeof setTimeout> | null = null;
  showDeleteModal = signal(false);
  editLoading = signal(false);
  editLoadError = signal<string | null>(null);
  private editRequestId = '';
  selectedConcept = signal<ConceptResponse | null>(null);
  isSaving = signal(false);
  saveError = signal<string | null>(null);
  saveSuccess = signal<string | null>(null);

  readonly languages = SUPPORTED_CONTENT_LANGUAGES;

  // Multi-select state — languages
  createSelectedLangs = signal<string[]>([]);
  editSelectedLangs = signal<string[]>([]);
  createLangOpen = signal(false);
  editLangOpen = signal(false);

  // Tags state
  createTags = signal<string[]>([]);
  editTags = signal<string[]>([]);
  createTagInput = signal('');
  editTagInput = signal('');

  addTag(ctx: 'create' | 'edit', value: string): void {
    const tag = value.trim();
    if (!tag) return;
    const sig = ctx === 'create' ? this.createTags : this.editTags;
    const inputSig = ctx === 'create' ? this.createTagInput : this.editTagInput;
    if (!sig().includes(tag)) sig.update(t => [...t, tag]);
    inputSig.set('');
  }

  removeTag(ctx: 'create' | 'edit', tag: string): void {
    const sig = ctx === 'create' ? this.createTags : this.editTags;
    sig.update(t => t.filter(x => x !== tag));
  }

  onTagKeydown(ctx: 'create' | 'edit', event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const inputSig = ctx === 'create' ? this.createTagInput : this.editTagInput;
      this.addTag(ctx, inputSig());
    }
  }

  createForm = this.fb.group({
    title: ['', [Validators.required, Validators.minLength(1)]],
    prompt: ['', [Validators.required, Validators.minLength(1)]],
    generate_image: [true],
    generate_body: [true],
  });

  editForm = this.fb.group({
    title: ['', [Validators.required, Validators.minLength(1)]],
    prompt: ['', [Validators.required, Validators.minLength(1)]],
    is_active: [true],
    generate_image: [true],
    generate_body: [true],
  });

  // Track form value changes as a signal so computed() can react to user typing
  private editFormValue = toSignal(this.editForm.valueChanges, { initialValue: this.editForm.value });

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadApps();
  }

  ngOnDestroy(): void {
    this.clearTimer(this.createCloseTimer);
    this.clearTimer(this.appMenuTimer);
    this.clearTimer(this.tagMenuTimer);
  }

  @HostListener('document:click')
  closeMenusFromOutside(): void {
    this.closeAppMenu();
    this.closeTagMenu();
  }

  @HostListener('document:keydown.escape')
  closeMenusFromEscape(): void {
    this.closeAppMenu();
    this.closeTagMenu();
  }

  loadApps(): void {
    this.appsLoading.set(true);
    this.appService.getApps(0, 100).subscribe({
      next: list => {
        this.apps.set(list);
        this.appsLoading.set(false);
        this.appsReady.set(true);
      },
      error: () => {
        this.appsLoading.set(false);
        this.appsReady.set(true);
      },
    });
  }

  fadeStageOut(event: AnimationCallbackEvent): void {
    const el = event.target as HTMLElement;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      event.animationComplete();
      return;
    }
    el.classList.add('concepts-fade-out');
    for (const child of el.querySelectorAll('*')) {
      for (const running of child.getAnimations()) running.cancel();
    }
    const animation = el.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 220, easing: 'ease', fill: 'forwards' },
    );
    animation.finished.then(
      () => event.animationComplete(),
      () => event.animationComplete(),
    );
  }

  onAppChange(appId: string): void {
    if (appId === this.selectedAppId()) return;
    const slug = appId ? slugForApp(appId, this.apps()) : '';
    void this.router.navigate(slug ? ['/prompts', slug] : ['/prompts']);
  }

  appMenuLabel(): string {
    return this.selectedApp()?.app_name || 'All apps';
  }

  tagMenuLabel(): string {
    return this.selectedTag() || 'All tags';
  }

  toggleAppMenu(): void {
    if (this.appMenuOpen()) {
      this.closeAppMenu();
      return;
    }
    this.closeTagMenu();
    this.clearTimer(this.appMenuTimer);
    this.appMenuTimer = null;
    this.appMenuClosing.set(false);
    this.appMenuOpen.set(true);
  }

  toggleTagMenu(): void {
    if (this.availableTags().length === 0) return;
    if (this.tagMenuOpen()) {
      this.closeTagMenu();
      return;
    }
    this.closeAppMenu();
    this.clearTimer(this.tagMenuTimer);
    this.tagMenuTimer = null;
    this.tagMenuClosing.set(false);
    this.tagMenuOpen.set(true);
  }

  chooseApp(appId: string): void {
    this.onAppChange(appId);
    this.closeAppMenu();
  }

  chooseTag(tag: string): void {
    this.selectedTag.set(tag);
    this.closeTagMenu();
  }

  closeAppMenu(): void {
    this.appMenuTimer = this.beginClose(this.appMenuOpen, this.appMenuClosing, this.appMenuTimer, () => {
      this.appMenuOpen.set(false);
      this.appMenuClosing.set(false);
      this.appMenuTimer = null;
    });
  }

  closeTagMenu(): void {
    this.tagMenuTimer = this.beginClose(this.tagMenuOpen, this.tagMenuClosing, this.tagMenuTimer, () => {
      this.tagMenuOpen.set(false);
      this.tagMenuClosing.set(false);
      this.tagMenuTimer = null;
    });
  }

  private syncSelection(slug: string, apps: RegisteredAppResponse[]): void {
    if (!slug) {
      this.applyApp('');
      if (this.errorMsg() === 'No app matches this address.') this.errorMsg.set(null);
      return;
    }
    const match = apps.find(app => slugForApp(app.app_id, apps) === slug);
    if (!match) {
      this.applyApp('');
      this.errorMsg.set('No app matches this address.');
      return;
    }
    this.applyApp(match.app_id);
  }

  private applyApp(appId: string): void {
    if (this.selectedAppId() === appId) return;
    this.selectedTag.set('');
    this.errorMsg.set(null);
    this.selectedAppId.set(appId);
    if (!appId) {
      this.concepts.set([]);
      return;
    }
    this.loadConceptsForApp(appId);
  }

  loadConceptsForApp(appId: string): void {
    this.isLoading.set(true);
    this.conceptService.list({ app_id: appId, skip: 0, limit: 100 }).subscribe({
      next: list => {
        this.concepts.set(list);
        this.isLoading.set(false);
      },
      error: err => {
        this.errorMsg.set(err?.error?.detail ?? 'Failed to load concepts.');
        this.isLoading.set(false);
      },
    });
  }

  refreshConcepts(): void {
    const appId = this.selectedAppId();
    if (appId) this.loadConceptsForApp(appId);
  }

  // ── Modals ────────────────────────────────────────────────────────────────
  // ── Language multi-select helpers ─────────────────────────────────────────
  toggleLang(ctx: 'create' | 'edit', code: string): void {
    const sig = ctx === 'create' ? this.createSelectedLangs : this.editSelectedLangs;
    sig.update(l => l.includes(code) ? l.filter(x => x !== code) : [...l, code]);
  }

  removeLang(ctx: 'create' | 'edit', code: string): void {
    const sig = ctx === 'create' ? this.createSelectedLangs : this.editSelectedLangs;
    sig.update(l => l.filter(x => x !== code));
  }

  getLangName(code: string): string {
    return SUPPORTED_CONTENT_LANGUAGES.find(l => l.code === code)?.name ?? code;
  }

  // ── Modals ────────────────────────────────────────────────────────────────
  openCreateModal(): void {
    this.clearTimer(this.createCloseTimer);
    this.createCloseTimer = null;
    this.createClosing.set(false);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.createSelectedLangs.set([]);
    this.createLangOpen.set(false);
    this.createTags.set([]);
    this.createTagInput.set('');
    this.createForm.reset({ title: '', prompt: '' });
    this.showCreateModal.set(true);
  }

  closeCreateModal(): void {
    if (!this.showCreateModal() || this.createClosing()) return;
    this.createLangOpen.set(false);
    this.createClosing.set(true);
    this.createCloseTimer = setTimeout(() => {
      this.showCreateModal.set(false);
      this.createClosing.set(false);
      this.createCloseTimer = null;
    }, 200);
  }

  private beginClose(
    open: { (): boolean },
    closing: { (): boolean; set(value: boolean): void },
    timer: ReturnType<typeof setTimeout> | null,
    finish: () => void,
  ): ReturnType<typeof setTimeout> | null {
    if (!open() || closing()) return timer;
    closing.set(true);
    this.clearTimer(timer);
    return setTimeout(finish, 200);
  }

  private clearTimer(timer: ReturnType<typeof setTimeout> | null): void {
    if (timer) clearTimeout(timer);
  }

  submitCreate(): void {
    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }
    const v = this.createForm.value;
    const payload: CreateConceptRequest = {
      app_id: this.selectedAppId(),
      title: v.title!,
      prompt: v.prompt!,
      languages: this.createSelectedLangs(),
      tags: this.createTags(),
      generate_image: v.generate_image ?? true,
      generate_body: v.generate_body ?? true,
    };
    this.isSaving.set(true);
    this.saveError.set(null);
    this.conceptService.create(payload).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.closeCreateModal();
        this.loadConceptsForApp(this.selectedAppId());
      },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to create concept.'); },
    });
  }

  private editInitial = signal<{ title: string; prompt: string; is_active: boolean; generate_image: boolean; generate_body: boolean; langs: string[]; tags: string[] } | null>(null);

  hasEditChanges = computed(() => {
    const init = this.editInitial();
    if (!init) return false;
    const v = this.editFormValue(); // signal-tracked so computed re-runs on every keystroke
    const langsChanged = JSON.stringify([...this.editSelectedLangs()].sort()) !== JSON.stringify([...init.langs].sort());
    const tagsChanged = JSON.stringify([...this.editTags()].sort()) !== JSON.stringify([...init.tags].sort());
    return (
      v.title !== init.title ||
      v.prompt !== init.prompt ||
      v.is_active !== init.is_active ||
      v.generate_image !== init.generate_image ||
      v.generate_body !== init.generate_body ||
      langsChanged ||
      tagsChanged
    );
  });

  openPrompt(concept: ConceptResponse): void {
    const slug = slugForApp(concept.app_id, this.apps()) || this.routeSlug();
    if (!slug) return;
    void this.router.navigate(['/prompts', slug, concept.concept_id]);
  }

  canDeactivate(): boolean {
    if (!this.routeConceptId() || !this.hasEditChanges()) return true;
    return window.confirm('You have unsaved changes. Leave this page?');
  }

  backToPrompts(): void {
    const slug = this.routeSlug();
    void this.router.navigate(slug ? ['/prompts', slug] : ['/prompts']);
  }

  private loadConceptForEdit(conceptId: string): void {
    if (this.editRequestId === conceptId) return;
    this.editRequestId = conceptId;
    this.editLoading.set(true);
    this.editLoadError.set(null);
    this.conceptService.get(conceptId).subscribe({
      next: concept => {
        const appId = this.selectedAppId();
        if (!appId || concept.app_id !== appId) {
          this.editLoadError.set('This prompt does not belong to this app.');
          this.editLoading.set(false);
          return;
        }
        this.populateEdit(concept);
        this.editLoading.set(false);
      },
      error: err => {
        this.editLoadError.set(err?.error?.detail ?? 'Failed to load prompt.');
        this.editLoading.set(false);
      },
    });
  }

  private populateEdit(concept: ConceptResponse): void {
    const langs = concept.languages ?? [];
    const tags = concept.tags ?? [];
    this.selectedConcept.set(concept);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    this.editSelectedLangs.set(langs);
    this.editLangOpen.set(false);
    this.editTags.set([...tags]);
    this.editTagInput.set('');
    this.inferenceLanguage.set(langs[0] ?? '');
    this.inferenceResult.set(null);
    this.inferenceError.set(null);
    this.editForm.reset({
      title: concept.title,
      prompt: concept.prompt,
      is_active: concept.is_active,
      generate_image: concept.generate_image,
      generate_body: concept.generate_body,
    });
    this.editInitial.set({
      title: concept.title,
      prompt: concept.prompt,
      is_active: concept.is_active,
      generate_image: concept.generate_image,
      generate_body: concept.generate_body,
      langs,
      tags,
    });
  }

  submitEdit(): void {
    const concept = this.selectedConcept();
    if (!concept || this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
    const v = this.editForm.value;
    const payload: UpdateConceptRequest = {
      title: v.title!,
      prompt: v.prompt!,
      languages: this.editSelectedLangs(),
      tags: this.editTags(),
      is_active: v.is_active ?? null,
      generate_image: v.generate_image ?? null,
      generate_body: v.generate_body ?? null,
    };
    this.isSaving.set(true);
    this.saveError.set(null);
    this.conceptService.update(concept.concept_id, payload).subscribe({
      next: updated => {
        this.selectedConcept.set(updated);
        this.editInitial.set({
          title: updated.title,
          prompt: updated.prompt,
          is_active: updated.is_active,
          generate_image: updated.generate_image,
          generate_body: updated.generate_body,
          langs: updated.languages ?? [],
          tags: updated.tags ?? [],
        });
        this.isSaving.set(false);
        this.saveSuccess.set('Saved.');
      },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to update concept.'); },
    });
  }

  openDeleteModal(concept: ConceptResponse): void {
    this.saveError.set(null);
    this.showDeleteModal.set(true);
    if (!this.selectedConcept()) this.selectedConcept.set(concept);
  }

  closeDeleteModal(): void { this.showDeleteModal.set(false); }

  confirmDelete(): void {
    const concept = this.selectedConcept();
    if (!concept) return;
    this.isSaving.set(true);
    this.saveError.set(null);
    this.conceptService.delete(concept.concept_id).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.showDeleteModal.set(false);
        this.editInitial.set(null);
        this.backToPrompts();
      },
      error: err => { this.isSaving.set(false); this.saveError.set(err?.error?.detail ?? 'Failed to delete concept.'); },
    });
  }

  getAppName(appId: string): string {
    return this.apps().find(a => a.app_id === appId)?.app_name ?? appId;
  }
}

const TR_FOLDS: Record<string, string> = {
  ı: 'i', İ: 'i', i: 'i', I: 'i',
  ş: 's', Ş: 's',
  ğ: 'g', Ğ: 'g',
  ü: 'u', Ü: 'u',
  ö: 'o', Ö: 'o',
  ç: 'c', Ç: 'c',
};

function slugifyAppName(name: string): string {
  const folded = [...name.trim()].map(ch => TR_FOLDS[ch] ?? ch).join('');
  const slug = folded
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'app';
}

function slugForApp(appId: string, apps: RegisteredAppResponse[]): string {
  const slugs = appSlugMap(apps);
  return slugs.get(appId) ?? '';
}

interface ZeroShotResult {
  generated_title?: string;
  generated_content?: string;
  language?: string;
  model?: string;
  prompt_sent?: string;
}

type InferenceView =
  | { kind: 'raw'; text: string }
  | {
      kind: 'article';
      title: string;
      paragraphs: string[];
      language: string;
      model: string;
      promptSent: string;
      json: string;
    };

function appSlugMap(apps: RegisteredAppResponse[]): Map<string, string> {
  const sorted = [...apps].sort((a, b) =>
    a.app_name.localeCompare(b.app_name) || a.app_id.localeCompare(b.app_id));
  const used = new Set<string>();
  const byId = new Map<string, string>();
  for (const app of sorted) {
    const base = slugifyAppName(app.app_name);
    let slug = base;
    if (used.has(slug)) slug = `${base}-${app.app_id.slice(0, 6)}`;
    if (used.has(slug)) slug = `${base}-${app.app_id}`;
    used.add(slug);
    byId.set(app.app_id, slug);
  }
  return byId;
}
